import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk");
      nav("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gagal login";
      toast.error(typeof msg === "string" ? msg : "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left: Brand */}
        <div className="hidden lg:block space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-14 w-24 flex items-center justify-center">
              <img src="/logo-rtm.png" alt="Rizki Service" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-slate-900">Rizki Service</div>
              <div className="text-xs uppercase tracking-widest text-slate-500">PT Rizki Teknologi Maju</div>
            </div>
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
            Kelola semua barang<br />service Anda di satu tempat.
          </h1>
          <p className="text-slate-600 leading-relaxed">
            Catat barang masuk, lacak status perbaikan (masuk → proses → selesai → diambil),
            cetak nota tanda terima, dan lihat laporan bulanan bisnis Anda — semua dari HP atau desktop.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4">
            {[
              { label: "Nota Otomatis", desc: "Cetak & unduh PDF" },
              { label: "Multi Peran", desc: "Admin & Teknisi" },
              { label: "Statistik", desc: "Dashboard real-time" },
              { label: "Mobile-First", desc: "Akses lewat HP" },
            ].map((f) => (
              <div key={f.label} className="rounded-lg border border-slate-200 bg-white/70 p-3">
                <div className="font-semibold text-slate-900 text-sm">{f.label}</div>
                <div className="text-xs text-slate-500">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Form */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="lg:hidden flex items-center gap-2 mb-5">
            <div className="h-10 w-16 flex items-center justify-center">
              <img src="/logo-rtm.png" alt="Rizki Service" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">Rizki Service</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">PT Rizki Teknologi Maju</div>
            </div>
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Masuk ke akun Anda</h2>
          <p className="text-sm text-slate-500 mb-6">Gunakan email & password yang terdaftar.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="anda@email.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-11"
            >
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-login-button"
              className="w-full h-11 gap-2 border-slate-300 hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Masuk dengan Google
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
